import { useState } from 'react';

import {
  useCreateWebhook,
  useDeleteWebhook,
  useWebhooks,
  type WebhookRow,
} from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  type Column,
  ConfirmModal,
  DataTable,
  DrawerPanel,
  EmptyState,
  Field,
  IconButton,
  Input,
  PageHeader,
  Toggle,
  formatDate,
} from '../../components/ui/index.js';

const ALL_EVENTS = [
  'ticket.created',
  'ticket.assigned',
  'ticket.closed',
  'ticket.escalated',
  'message.created',
  'contact.created',
  'contact.updated',
  'csat.submitted',
];

export function SettingsWebhooks(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const webhooksQ = useWebhooks(slug);
  const createWebhook = useCreateWebhook(slug);
  const deleteWebhook = useDeleteWebhook(slug);
  const list = webhooksQ.data ?? [];

  const [showCreate, setShowCreate] = useState(false);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  function toggleEvent(event: string): void {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  }

  async function handleCreate(): Promise<void> {
    if (!url.trim() || selectedEvents.length === 0) return;
    await createWebhook.mutateAsync({
      url: url.trim(),
      events: selectedEvents,
      name: name.trim() || undefined,
    });
    setShowCreate(false);
    setUrl('');
    setName('');
    setSelectedEvents([]);
  }

  const columns: Column<WebhookRow>[] = [
    {
      key: 'url',
      label: 'URL',
      render: (r) => (
        <span style={{ fontFamily: 'var(--inbx-font-mono)', fontSize: 'var(--inbx-fz-sm)' }}>
          {r.url}
        </span>
      ),
    },
    {
      key: 'events',
      label: 'События',
      render: (r) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(r.events ?? []).slice(0, 4).map((e) => (
            <span key={e} className="inbx-pill-badge inbx-pill-badge--default">
              {e}
            </span>
          ))}
          {(r.events?.length ?? 0) > 4 && (
            <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-xs)' }}>
              +{r.events.length - 4}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Статус',
      width: 100,
      render: (r) => (
        <span className={`inbx-pill-badge inbx-pill-badge--${r.isActive ? 'success' : 'default'}`}>
          {r.isActive ? 'активен' : 'выключен'}
        </span>
      ),
    },
    { key: 'created', label: 'Создан', width: 130, render: (r) => formatDate(r.createdAt) },
    {
      key: 'actions',
      label: '',
      width: 80,
      render: (r) => (
        <span style={{ display: 'flex', gap: 2 }}>
          <IconButton icon="ti-trash" tooltip="Удалить" onClick={() => setDeleteId(r.id)} />
        </span>
      ),
    },
  ];

  return (
    <div className="inbx-page">
      <PageHeader
        title="Webhooks"
        actions={
          <Button variant="primary" icon="ti-plus" onClick={() => setShowCreate(true)}>
            Добавить endpoint
          </Button>
        }
      />
      <div className="inbx-page__body" style={{ padding: list.length === 0 ? 24 : 0 }}>
        {list.length === 0 && !webhooksQ.isLoading ? (
          <EmptyState
            icon="ti-webhook"
            title="Endpoints не настроены"
            description="Webhook отправляет события (новый тикет, ответ клиента и т.д.) на ваш URL"
          />
        ) : (
          <DataTable rows={list} columns={columns} loading={webhooksQ.isLoading} />
        )}
      </div>

      <DrawerPanel
        open={showCreate}
        title="Новый webhook"
        onClose={() => setShowCreate(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createWebhook.isPending || !url.trim() || selectedEvents.length === 0}
            >
              {createWebhook.isPending ? 'Создание…' : 'Создать'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Endpoint URL" required>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-server.com/webhook"
              autoFocus
            />
          </Field>
          <Field label="Название" hint="необязательно">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Мой webhook"
            />
          </Field>
          <Field label="События" required hint="выберите хотя бы одно">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {ALL_EVENTS.map((event) => (
                <div key={event} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Toggle
                    checked={selectedEvents.includes(event)}
                    onChange={() => toggleEvent(event)}
                    label={event}
                  />
                </div>
              ))}
            </div>
          </Field>
        </div>
      </DrawerPanel>

      <ConfirmModal
        open={!!deleteId}
        title="Удалить webhook?"
        description="Endpoint будет удалён и перестанет получать события."
        confirmLabel="Удалить"
        variant="danger"
        onConfirm={() => {
          if (deleteId) deleteWebhook.mutate(deleteId);
          setDeleteId(null);
        }}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
